import { css } from "@emotion/react";
import { useId } from "react";

const iconWrapCSS = css`
  display: inline-flex;
  flex-shrink: 0;
`;

import type { SandboxBackendType } from "@phoenix/types";

type IconProps = { height: number };

const MontySVG = ({ height }: IconProps) => (
  <svg
    viewBox="0 0 120 120"
    width={height}
    height={height}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Monty</title>
    <path d="M 119.18,86.64 98.02,57.3 c 0,0 0,0 0,0 L 63.77,9.8 c -1.74,-2.4 -5.76,-2.4 -7.49,0 l -34.24,47.49 c 0,0 0,0 0,0 L 0.87,86.64 c -0.86,1.2 -1.1,2.73 -0.65,4.13 0.46,1.4 1.55,2.5 2.95,2.96 l 55.41,18.14 c 0,0 0,0 0.01,9e-4 0.46,0.15 0.94,0.23 1.43,0.23 0.49,0 0.97,-0.08 1.43,-0.23 0,0 0,0 0.01,0 L 116.87,93.73 c 1.4,-0.46 2.5,-1.55 2.95,-2.96 0.46,-1.4 0.22,-2.93 -0.65,-4.13 z m -59.15,-66.25 22.21,30.8 -20.77,-6.8 c -0.16,-0.05 -0.33,-0.04 -0.49,-0.08 -0.16,-0.04 -0.32,-0.06 -0.48,-0.08 -0.16,-0.02 -0.31,-0.08 -0.47,-0.08 -0.16,0 -0.31,0.06 -0.47,0.08 -0.17,0.02 -0.32,0.04 -0.48,0.08 -0.16,0.03 -0.33,0.03 -0.48,0.08 h 0 l -20.64,6.76 -0.13,0.04 22.21,-30.8 z m -31.38,43.52 24.18,-7.92 2.58,-0.84 V 101.12 L 12.06,86.92 Z m 36,37.2 V 55.15 l 26.76,8.76 16.59,23 z" />
  </svg>
);

const DaytonaSVG = ({ height }: IconProps) => (
  <svg
    viewBox="0 -0.5 27 27"
    width={height}
    height={height}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Daytona</title>
    <path d="M 1.412 17.375 L 11.078 17.375 L 11.078 20.423 L 1.412 20.423 Z M 14.39 6.707 L 25.436 6.707 L 25.436 9.755 L 14.39 9.755 Z M 8.591 7.647 L 16.792 0.103 L 19.135 2.259 L 10.935 9.802 Z" />
    <path d="M 8.642 15.267 L 2.343 9.473 L 0 11.628 L 6.298 17.422 Z M 16.927 19.53 L 10.288 25.637 L 7.944 23.482 L 14.584 17.375 Z M 16.876 11.911 L 24.296 18.736 L 26.639 16.58 L 19.219 9.755 Z M 8.591 4.421 L 8.591 11.787 L 5.277 11.787 L 5.277 4.421 Z" />
    <path d="M 20.19 15.089 L 20.19 24.233 L 16.877 24.233 L 16.877 15.089 Z" />
  </svg>
);

// Gradient IDs are namespaced via useId() so multiple Modal icons on the
// same page don't collide on the SVG <defs>.
const ModalSVG = ({ height }: IconProps) => {
  const uid = useId();
  const g = (n: number) => `modal-grad-${uid}-${n}`;
  return (
    <svg
      viewBox="0 -46 192 192"
      width={height}
      height={height}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Modal</title>
      <path
        d="M77.5378 2.08333L95.5795 33.3333L58.293 97.9167C57.5482 99.2031 56.1732 100 54.6836 100H21.0065C20.2617 100 19.5482 99.8021 18.9232 99.4427C18.2982 99.0833 17.7722 98.5625 17.3972 97.9167L0.558594 68.75C-0.186198 67.4635 -0.186198 65.875 0.558594 64.5833L36.6472 2.08333C37.0169 1.4375 37.5482 0.91667 38.1732 0.557295C38.7982 0.19792 39.5117 0 40.2565 0H73.9336C75.4232 0 76.7982 0.796875 77.543 2.08333H77.5378ZM190.605 64.5833L154.517 2.08333C154.147 1.4375 153.616 0.91667 152.991 0.557295C152.366 0.19792 151.652 0 150.908 0H117.23C115.741 0 114.366 0.796875 113.621 2.08333L95.5795 33.3333L132.866 97.9167C133.611 99.2031 134.986 100 136.475 100H170.152C170.897 100 171.611 99.8021 172.236 99.4427C172.861 99.0833 173.387 98.5625 173.762 97.9167L190.6 68.75C191.345 67.4635 191.345 65.875 190.6 64.5833H190.605Z"
        fill="#62DE61"
      />
      <path
        d="M57.0948 33.3333H95.5844L77.5427 2.08333C76.7979 0.796875 75.4229 0 73.9333 0H40.2562C39.5114 0 38.7979 0.19792 38.1729 0.557295L57.0948 33.3333Z"
        fill={`url(#${g(0)})`}
      />
      <path
        d="M57.0951 33.3333L38.1732 0.557312C37.5482 0.916687 37.0222 1.43752 36.6472 2.08335L0.558594 64.5834C-0.186198 65.875 -0.186198 67.4583 0.558594 68.75L17.3972 97.9167C17.767 98.5625 18.2982 99.0833 18.9232 99.4427L57.0899 33.3333H57.0951Z"
        fill={`url(#${g(1)})`}
      />
      <path
        d="M95.5796 33.3334H57.09L18.9233 99.4427C19.5483 99.8021 20.2619 100 21.0067 100H54.6838C56.1734 100 57.5483 99.2032 58.2931 97.9167L95.5796 33.3334Z"
        fill="#09AF58"
      />
      <path
        d="M190.606 68.75C190.976 68.1042 191.163 67.3854 191.163 66.6667H153.319L134.397 99.4427C135.022 99.8021 135.736 100 136.481 100H170.158C171.647 100 173.022 99.2031 173.767 97.9167L190.606 68.75Z"
        fill="#09AF58"
      />
      <path
        d="M117.23 0C116.486 0 115.772 0.19792 115.147 0.557295L153.314 66.6667H191.157C191.157 65.9479 190.97 65.2292 190.6 64.5833L154.517 2.08333C153.772 0.796875 152.397 0 150.907 0H117.225H117.23Z"
        fill={`url(#${g(2)})`}
      />
      <path
        d="M134.392 99.4427L153.314 66.6667L115.147 0.557312C114.522 0.916687 113.996 1.43752 113.621 2.08335L95.5796 33.3333L132.866 97.9167C133.236 98.5625 133.767 99.0833 134.392 99.4427Z"
        fill={`url(#${g(3)})`}
      />
      <defs>
        <linearGradient
          id={g(0)}
          x1="81.1468"
          y1="41.6667"
          x2="52.6052"
          y2="-7.77607"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#BFF9B4" />
          <stop offset="1" stopColor="#80EE64" />
        </linearGradient>
        <linearGradient
          id={g(1)}
          x1="4.49089"
          y1="91.1094"
          x2="52.1211"
          y2="8.61458"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#80EE64" />
          <stop offset="0.18" stopColor="#7BEB63" />
          <stop offset="0.36" stopColor="#6FE562" />
          <stop offset="0.55" stopColor="#5ADA60" />
          <stop offset="0.74" stopColor="#3DCA5D" />
          <stop offset="0.93" stopColor="#18B759" />
          <stop offset="1" stopColor="#09AF58" />
        </linearGradient>
        <linearGradient
          id={g(2)}
          x1="177.209"
          y1="74.7187"
          x2="129.579"
          y2="-7.77606"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#BFF9B4" />
          <stop offset="1" stopColor="#80EE64" />
        </linearGradient>
        <linearGradient
          id={g(3)}
          x1="148.345"
          y1="91.3906"
          x2="100.715"
          y2="8.8907"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#80EE64" />
          <stop offset="0.18" stopColor="#7BEB63" />
          <stop offset="0.36" stopColor="#6FE562" />
          <stop offset="0.55" stopColor="#5ADA60" />
          <stop offset="0.74" stopColor="#3DCA5D" />
          <stop offset="0.93" stopColor="#18B759" />
          <stop offset="1" stopColor="#09AF58" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const E2BSVG = ({ height }: IconProps) => (
  <svg
    viewBox="-1 0 28 28"
    width={height}
    height={height}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>E2B</title>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M21.8458 19.3029C21.6671 19.3029 21.5555 19.4963 21.6448 19.6511L23.5141 22.889C23.6175 23.0681 23.4528 23.2828 23.253 23.2293L17.5836 21.7101C17.3359 21.6437 17.0813 21.7907 17.0149 22.0384L15.4958 27.7079C15.4422 27.9077 15.1739 27.943 15.0705 27.7639L13.2008 24.5254C13.1115 24.3707 12.8881 24.3707 12.7987 24.5254L10.929 27.7639C10.8256 27.943 10.5573 27.9077 10.5038 27.7079L8.9846 22.0384C8.91824 21.7907 8.66365 21.6437 8.41597 21.7101L2.74652 23.2293C2.54675 23.2828 2.38199 23.0681 2.4854 22.889L4.35472 19.6511C4.44406 19.4963 4.33238 19.3029 4.15368 19.3029L0.415222 19.3028C0.208406 19.3028 0.104834 19.0528 0.251077 18.9066L4.40145 14.7563C4.58277 14.5749 4.58277 14.281 4.40145 14.0997L0.251079 9.94927C0.104837 9.80302 0.208414 9.55297 0.415232 9.55297L4.15328 9.55302C4.33198 9.55302 4.44368 9.35957 4.35433 9.20481L2.4854 5.96763C2.38199 5.78852 2.54676 5.5738 2.74652 5.62733L8.41597 7.14652C8.66365 7.21288 8.91824 7.0659 8.98461 6.81822L10.5038 1.14869C10.5573 0.948918 10.8256 0.913592 10.929 1.0927L12.7987 4.33116C12.8881 4.48593 13.1114 4.48593 13.2008 4.33116L15.0705 1.0927C15.1739 0.913592 15.4422 0.948917 15.4957 1.14869L17.0149 6.81822C17.0813 7.0659 17.3359 7.21288 17.5835 7.14652L23.253 5.62733C23.4528 5.5738 23.6175 5.78852 23.5141 5.96763L21.6452 9.20481C21.5558 9.35957 21.6675 9.55302 21.8462 9.55302L25.5844 9.55297C25.7912 9.55297 25.8948 9.80302 25.7486 9.94927L21.5982 14.0997C21.4169 14.281 21.4169 14.5749 21.5982 14.7563L25.7486 18.9066C25.8948 19.0528 25.7912 19.3028 25.5844 19.3028L21.8458 19.3029ZM20.419 10.404C20.5869 10.236 20.4241 9.9541 20.1947 10.0156L15.1461 11.3684C14.8984 11.4348 14.6438 11.2878 14.5775 11.0401L13.224 5.98888C13.1625 5.75947 12.837 5.75947 12.7755 5.98888L11.422 11.0401C11.3557 11.2878 11.1011 11.4348 10.8534 11.3684L5.80496 10.0156C5.57555 9.95414 5.41278 10.2361 5.58072 10.404L9.27643 14.0997C9.45774 14.281 9.45774 14.575 9.27643 14.7563L5.57985 18.4528C5.41191 18.6208 5.57467 18.9027 5.80409 18.8412L10.8534 17.4882C11.1011 17.4218 11.3557 17.5688 11.422 17.8165L12.7755 22.8677C12.837 23.0972 13.1625 23.0972 13.224 22.8677L14.5775 17.8165C14.6439 17.5688 14.8984 17.4218 15.1461 17.4882L20.1956 18.8413C20.425 18.9027 20.5878 18.6208 20.4198 18.4529L16.7232 14.7563C16.5419 14.575 16.5419 14.281 16.7232 14.0997L20.419 10.404Z"
    />
  </svg>
);

const VercelSVG = ({ height }: IconProps) => (
  <svg
    viewBox="0 -17 256 256"
    width={height}
    height={height}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Vercel</title>
    <polygon points="128 0 256 221.705007 0 221.705007" />
  </svg>
);

const DenoSVG = ({ height }: IconProps) => (
  <svg
    viewBox="0 0 128 128"
    width={height}
    height={height}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Deno</title>
    <path d="M64 2C29.755 2 2 29.755 2 64c0 34.245 27.755 62 62 62 34.245 0 62-27.755 62-62 0-34.245-27.755-62-62-62zm32.09 20.126.023-.073.799-3.003.363.266a53.424 53.424 0 0 1 4.771 3.997l-.097.314-.022.073a3.03 3.03 0 0 1-3.488 2.01 3.02 3.02 0 0 1-2.349-3.584zm-13.853 5.716.023-.073c.46-1.55 2.107-2.47 3.705-2.059 1.574.436 2.519 2.059 2.131 3.657v.073l-3.245 11.988-.266-.218a32.286 32.286 0 0 0-5.11-3.1zM71.484 14.497l.023-.073 1.453-5.4.436.072c1.84.315 3.681.727 5.473 1.211l-1.526 5.691-.022.073a3.03 3.03 0 0 1-3.488 2.01 3.02 3.02 0 0 1-2.349-3.584zM49.009 23.7l.023-.073 3.826-14.216.412-.072a57.938 57.938 0 0 1 5.764-.824c.097.436.097.92-.023 1.356l-.022.073-4.117 15.258-.023.073a3.03 3.03 0 0 1-3.488 2.01 3.02 3.02 0 0 1-2.349-3.584zM13.843 56.395c-.46 1.55-2.107 2.47-3.681 2.058a2.867 2.867 0 0 1-1.502-.92 55.517 55.517 0 0 1 6.467-20.295c.242 0 .484.023.726.097a3.042 3.042 0 0 1 2.156 3.657l-.023.073-4.117 15.258zm12.4 8.33v.074l-4.117 15.258-.023.072c-.46 1.526-2.034 2.422-3.608 2.059-1.623-.388-2.616-2.034-2.229-3.657v-.073L20.384 63.2l.022-.073c.46-1.55 2.107-2.47 3.706-2.058 1.574.436 2.519 2.058 2.131 3.657zm1.187-20.78c-1.623-.387-2.616-2.034-2.228-3.656l.022-.073 4.117-15.258.023-.073c.46-1.55 2.107-2.47 3.681-2.058a3.028 3.028 0 0 1 2.156 3.633l-.023.096-4.117 15.258-.023.073c-.46 1.526-2.034 2.422-3.608 2.059zm7.992 52.096-.023.072c-.46 1.526-2.034 2.422-3.608 2.06-1.623-.388-2.616-2.035-2.228-3.658l.022-.073 4.117-15.258.023-.097.194-.46a27.216 27.216 0 0 0 5.231 3.56zm12.473 9.324-.023.073-2.93 10.923-.412-.17a51.943 51.943 0 0 1-5.207-2.252l2.712-10.075.023-.073a3.011 3.011 0 0 1 3.681-2.058c1.599.412 2.543 2.034 2.156 3.633zm16.929-7.726-.073.34v.072l-4.117 15.258-.023.073a3.03 3.03 0 0 1-3.487 2.01 3.032 3.032 0 0 1-2.35-3.584v-.073l4.118-15.258.022-.073a3.026 3.026 0 0 1 3.706-2.059 2.984 2.984 0 0 1 1.889 1.526l.097.436.145.702.097.51zm40.276 3.948c-8.622 9.421-20.441 15.863-33.737 17.631l-.121-.8-.218-1.598-.194-1.162-.218-1.526-.29-1.865-.121-.726-.266-1.768-.17-1.042-.218-1.356-.218-1.308-.218-1.26-.218-1.234-.194-1.211-.218-1.163-.194-1.114-.145-.823-.17-.8-.096-.508-.194-1.017-.146-.727-.169-.896-.145-.63-.12-.605-.122-.581-.073-.388-.17-.726-.12-.533-.121-.533-.097-.339-.12-.484-.098-.46-.12-.46-.121-.437-.073-.266-.121-.412-.097-.387-.073-.266-.097-.243-.072-.218-.097-.339-.073-.242-.049-.145a9.113 9.113 0 0 0-.435-1.138l-.073-.145.557-1.454-2.204.073-.605.023c-20.006.412-32.915-8.09-32.915-21.409 0-14.12 14.047-25.478 32.066-25.478 8.67 0 16.105 2.398 21.966 6.975 4.989 3.9 8.646 9.276 10.535 15.355l.048.145.048.17.097.314.145.582.388 1.356.411 1.55.703 2.567 1.114 4.069 1.792 6.684 2.035 7.605 3.269 12.23 1.235 4.601zm3.052-60.595-.023.073-4.117 15.258-.023.073c-.46 1.525-2.034 2.421-3.609 2.058-1.622-.387-2.615-2.034-2.228-3.657l.023-.073 4.117-15.258v-.072a3.078 3.078 0 0 1 3.705-2.059 3.062 3.062 0 0 1 2.156 3.657zm10.414 20.344-4.142 15.258v.073c-.436 1.623-2.107 2.567-3.73 2.131-1.622-.436-2.567-2.107-2.13-3.73l.022-.072 4.117-15.258.023-.073c.46-1.55 2.107-2.47 3.681-2.059a3.028 3.028 0 0 1 2.156 3.633zM69.329 51.164a3.875 3.875 0 0 1-3.875 3.875 3.875 3.875 0 0 1-3.875-3.875 3.875 3.875 0 0 1 3.875-3.875 3.875 3.875 0 0 1 3.875 3.875z" />
  </svg>
);

const WasmSVG = ({ height }: IconProps) => (
  <svg
    viewBox="0 0 128 128"
    width={height}
    height={height}
    fill="#654ff0"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>WebAssembly</title>
    <path d="M.223.222v127.555h127.555V.222H78.594c.014.227.036.455.036.686 0 8.08-6.55 14.626-14.63 14.626-8.078 0-14.625-6.546-14.625-14.626 0-.231.022-.459.031-.686zm29.595 68.746h8.445l5.782 30.738h.107l6.968-30.738h7.908l6.265 31.119h.106l6.597-31.119h8.284l-10.765 45.156H61.12l-6.213-30.738H54.8l-6.7 30.738h-8.557zm59.994 0h13.334l13.284 45.156h-8.77l-2.879-10.051H89.59l-2.212 10.05h-8.5ZM94.895 80.1l-3.684 16.57h11.473L98.448 80.1Z" />
  </svg>
);

const DockerSVG = ({ height }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    width={height}
    height={height}
    fill="#2496ED"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Docker</title>
    <path d="M13.983 11.078h2.119a.186.186 0 00.186-.185V9.006a.186.186 0 00-.186-.186h-2.119a.185.185 0 00-.185.185v1.888c0 .102.083.185.185.185m-2.954-5.43h2.118a.186.186 0 00.186-.186V3.574a.186.186 0 00-.186-.185h-2.118a.185.185 0 00-.185.185v1.888c0 .102.082.185.185.185m0 2.716h2.118a.187.187 0 00.186-.186V6.29a.186.186 0 00-.186-.185h-2.118a.185.185 0 00-.185.185v1.887c0 .102.082.185.185.186m-2.93 0h2.12a.186.186 0 00.184-.186V6.29a.185.185 0 00-.185-.185H8.1a.185.185 0 00-.185.185v1.887c0 .102.083.185.185.186m-2.964 0h2.119a.186.186 0 00.185-.186V6.29a.185.185 0 00-.185-.185H5.136a.186.186 0 00-.186.185v1.887c0 .102.084.185.186.186m5.893 2.715h2.118a.186.186 0 00.186-.185V9.006a.186.186 0 00-.186-.186h-2.118a.185.185 0 00-.185.185v1.888c0 .102.082.185.185.185m-2.93 0h2.12a.185.185 0 00.184-.185V9.006a.185.185 0 00-.184-.186h-2.12a.185.185 0 00-.184.185v1.888c0 .102.083.185.185.185m-2.964 0h2.119a.185.185 0 00.185-.185V9.006a.185.185 0 00-.184-.186h-2.12a.186.186 0 00-.186.186v1.887c0 .102.084.185.186.185m-2.92 0h2.12a.185.185 0 00.184-.185V9.006a.185.185 0 00-.184-.186h-2.12a.185.185 0 00-.184.185v1.888c0 .102.082.185.185.185M23.763 9.89c-.065-.051-.672-.51-1.954-.51-.338.001-.676.03-1.01.087-.248-1.7-1.653-2.53-1.716-2.566l-.344-.199-.226.327c-.284.438-.49.922-.612 1.43-.23.97-.09 1.882.403 2.661-.595.332-1.55.413-1.744.42H.751a.751.751 0 00-.75.748 11.376 11.376 0 00.692 4.062c.545 1.428 1.355 2.48 2.41 3.124 1.18.723 3.1 1.137 5.275 1.137.983.003 1.963-.086 2.93-.266a12.248 12.248 0 003.823-1.389c.98-.567 1.86-1.288 2.61-2.136 1.252-1.418 1.998-2.997 2.553-4.4h.221c1.372 0 2.215-.549 2.68-1.009.309-.293.55-.65.707-1.046l.098-.288Z" />
  </svg>
);

const ICONS_BY_BACKEND_TYPE: Record<SandboxBackendType, React.FC<IconProps>> = {
  WASM: WasmSVG,
  E2B: E2BSVG,
  DAYTONA: DaytonaSVG,
  VERCEL: VercelSVG,
  DENO: DenoSVG,
  MODAL: ModalSVG,
  MONTY: MontySVG,
  DOCKER: DockerSVG,
};

export type SandboxProviderIconProps = {
  backendType: SandboxBackendType;
  height?: number;
};

/**
 * Renders the brand icon for a sandbox provider. Returns null when no icon is
 * registered for the given provider kind, so callers can use it unconditionally.
 */
export function SandboxProviderIcon({
  backendType,
  height = 18,
}: SandboxProviderIconProps) {
  const Icon = ICONS_BY_BACKEND_TYPE[backendType];
  if (!Icon) {
    return null;
  }
  return (
    <span css={iconWrapCSS}>
      <Icon height={height} />
    </span>
  );
}
